import React, { useEffect, useState } from 'react';
import { useLanguage } from '../components/LanguageContext';
import { useProfileState } from '../components/ProfileStateContext';
import { ArrowRightIcon, ChevronLeft } from 'lucide-react';
import { TOPICS } from '../data/topics';
interface Props {
  onContinue: () => void;
  onSkip?: () => void;
  onBack?: () => void;
}
const MAX_SELECTIONS = 5;
export function OnboardingThemes({
  onContinue,
  onSkip,
  onBack
}: Props) {
  const {
    t
  } = useLanguage();
  const {
    updateDraft,
    selectedThemeIds
  } = useProfileState();
  // Initialize from context — filter only IDs that match canonical TOPICS
  const initialSelected = new Set<string>(selectedThemeIds.filter(id => TOPICS.some(tp => tp.id === id)));
  const [selected, setSelected] = useState<Set<string>>(initialSelected);
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(timer);
  }, []);
  const toggleChip = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (next.size >= MAX_SELECTIONS) return prev;
        next.add(id);
      }
      return next;
    });
  };
  const isValid = selected.size > 0;
  const maxReached = selected.size >= MAX_SELECTIONS;
  return <div className="flex flex-col h-full w-full bg-[#0D0D0D] relative" data-id="element-829">
      {onBack && <button onClick={onBack} aria-label="Back" className="absolute top-6 left-6 z-50 w-10 h-10 rounded-full bg-[#1A1A1A]/80 backdrop-blur-md border border-[#2A2A2A] text-[#888888] flex items-center justify-center hover:text-white transition-colors" data-id="element-830">
          <ChevronLeft className="w-6 h-6 -ml-0.5" strokeWidth={2} data-id="element-831" />
        </button>}
      <div className="flex-1 overflow-y-auto px-6 pt-20 pb-48" data-id="element-832">
        {/* Progress Bar */}
        <div className="w-full transition-all duration-700 ease-out" style={{
        opacity: mounted ? 1 : 0,
        transitionDelay: '0ms'
      }} data-id="element-833">
          <div className="w-full h-1 bg-[#1A1A1A] rounded-full overflow-hidden" data-id="element-834">
            <div className="h-full bg-[#C5D82D] w-[40%] rounded-full transition-all duration-500" data-id="element-835" />
          </div>
          <div className="flex items-center justify-end mt-1.5" data-id="element-836">
            <span className="text-[#888888] text-[12px]" data-id="element-837">
              {t('themes.progress')}
            </span>
          </div>
        </div>

        {/* Heading & Subtext */}
        <div className="mt-6 transition-all duration-700 ease-out" style={{
        opacity: mounted ? 1 : 0,
        transitionDelay: '120ms'
      }} data-id="element-838">
          <h1 className="text-[20px] text-white font-semibold" data-id="element-839">
            {t('themes.heading')}
          </h1>
          <p className="text-[14px] text-[#AAAAAA] mt-1" data-id="element-840">
            {t('themes.subtext')}
          </p>
        </div>

        {/* Chip Grid */}
        <div className="mt-5 flex flex-wrap gap-2 transition-all duration-700 ease-out" style={{
        opacity: mounted ? 1 : 0,
        transitionDelay: '250ms'
      }} data-id="element-841">
          {TOPICS.map(topic => {
          const isSelected = selected.has(topic.id);
          const label = t(topic.nameKey);
          const isDisabled = maxReached && !isSelected;
          return <button key={topic.id} onClick={() => {
            if (isDisabled) return;
            toggleChip(topic.id);
          }} disabled={isDisabled} title={isDisabled ? t('themes.maxReached') : undefined} className={`min-h-[44px] px-4 py-2.5 rounded-full text-[13px] border transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C5D82D] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D0D0D] ${isSelected ? 'bg-[rgba(197,216,45,0.1)] border-[#C5D82D] text-[#C5D82D] font-medium' : 'bg-[#1A1A1A] border-[#2A2A2A] text-white'} ${isDisabled ? 'opacity-40 cursor-not-allowed' : ''}`} aria-pressed={isSelected} data-id="element-842">
                {label}
              </button>;
        })}
        </div>
      </div>

      {/* Fixed Bottom Bar */}
      <div className="absolute bottom-0 left-0 right-0 bg-[#0D0D0D]/95 backdrop-blur-xl border-t border-[#1A1A1A] px-6 pt-4 pb-8 transition-all duration-700 ease-out z-40" style={{
      opacity: mounted ? 1 : 0,
      transitionDelay: '400ms'
    }} data-id="element-843">
        <div className="flex flex-col" data-id="element-844">
          <button onClick={onSkip} className="w-full mb-2 py-3 text-[13px] text-[#888888] hover:text-[#AAAAAA] transition-colors text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C5D82D] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D0D0D] rounded-lg" data-id="element-845">
            {t('themes.skip')}
          </button>
          <button onClick={() => {
          updateDraft({
            selectedThemeIds: Array.from(selected)
          });
          onContinue();
        }} disabled={!isValid} className={`w-full py-4 rounded-full border-[1.5px] border-[#C5D82D] text-[#C5D82D] bg-transparent text-[14px] font-semibold tracking-wide flex items-center justify-center gap-2 transition-colors hover:bg-[#C5D82D]/10 ${!isValid ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-846">
            {t('themes.cta')}
            <ArrowRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-847" />
          </button>
        </div>
      </div>
    </div>;
}